import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@wmart/ui";
import { api, ApiError } from "../../lib/api-client.js";
import { formatDate } from "../../lib/format.js";
import { useToast } from "../../lib/toast.js";
import { useAuth } from "../../lib/auth.js";
import type {
  PortalPoDetailDTO,
  SupplierOrderStatus,
} from "@wmart/shared-types";
import { PO_STATUS_LABEL, PO_STATUS_TONE } from "./po-status.js";

const API_BASE: string = (import.meta.env.VITE_API_URL as string) ?? "";

/**
 * The single forward action a supplier can take from each status. The PO
 * lifecycle is new → acknowledged → preparing → ready; terminal states
 * (ready / cancelled / closed) offer no action. The `next` value is the
 * status we POST to `/v1/supplier/orders/:id/status`.
 */
const NEXT_ACTION: Partial<
  Record<
    SupplierOrderStatus,
    { label: string; next: "acknowledged" | "preparing" | "ready" }
  >
> = {
  new: { label: "Acknowledge", next: "acknowledged" },
  acknowledged: { label: "Start preparing", next: "preparing" },
  preparing: { label: "Mark ready", next: "ready" },
};

/**
 * Supplier portal — one purchase order. Renders the PO header (number,
 * status, lifecycle dates), the line items, a single status-advancing action
 * button, and a "Download PDF" link. The status POST returns the refreshed
 * detail DTO, so we set state directly from the response (no refetch).
 */
export function SupplierPoDetail() {
  const { id } = useParams<{ id: string }>();
  const { notify, notifyProblem } = useToast();
  const { me } = useAuth();
  const [po, setPo] = useState<PortalPoDetailDTO | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trackingNote, setTrackingNote] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<PortalPoDetailDTO>(
        `/v1/supplier/orders/${id}`,
      );
      setPo(data);
      setLoadError(false);
    } catch (err) {
      setLoadError(true);
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load purchase order");
    }
  }, [id, notify, notifyProblem]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while the PDF is still rendering so the download button auto-enables
  // without a manual refresh. Only polls when the API explicitly reports the
  // PDF as not-ready (=== false); an older API that omits the field leaves it
  // undefined and we don't poll (the button stays enabled — prior behaviour).
  useEffect(() => {
    if (!po || po.pdf_ready !== false) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [po, load]);

  async function advance(
    next: "acknowledged" | "preparing" | "ready",
    label: string,
  ) {
    if (!id) return;
    setBusy(true);
    try {
      // The status endpoint returns the refreshed detail — set state from it
      // directly rather than issuing a second GET.
      const data = await api.post<PortalPoDetailDTO>(
        `/v1/supplier/orders/${id}/status`,
        { status: next },
      );
      setPo(data);
      notify("success", `${label} — ${data.po_number}`);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to update purchase order");
    } finally {
      setBusy(false);
    }
  }

  async function ship() {
    if (!id) return;
    setBusy(true);
    try {
      const body: { status: "shipped"; tracking_note?: string } = {
        status: "shipped",
      };
      if (trackingNote.trim()) body.tracking_note = trackingNote.trim();
      const data = await api.post<PortalPoDetailDTO>(
        `/v1/supplier/orders/${id}/status`,
        body,
      );
      setPo(data);
      notify("success", `Marked shipped — ${data.po_number}`);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to update purchase order");
    } finally {
      setBusy(false);
    }
  }

  if (loadError && !po) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to="/supplier/orders"
          className="text-body-sm text-accent hover:underline"
        >
          ← Back to purchase orders
        </Link>
        <p className="text-body-sm text-danger">
          Purchase order could not be loaded.
        </p>
      </div>
    );
  }

  if (!po) {
    return <p className="text-body-sm text-ink-500">Loading…</p>;
  }

  const action = NEXT_ACTION[po.status];
  const fulfilmentMode = me?.supplier?.fulfilmentMode;
  const isDropship = fulfilmentMode === "dropship";

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/supplier/orders"
        className="text-body-sm text-accent hover:underline"
      >
        ← Back to purchase orders
      </Link>

      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-serif text-display-sm text-ink-900">
                <span className="font-mono">{po.po_number}</span>
              </h1>
              <StatusPill tone={PO_STATUS_TONE[po.status]}>
                {PO_STATUS_LABEL[po.status]}
              </StatusPill>
            </div>
            <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-body-sm text-ink-500">
              <div className="flex gap-1.5">
                <dt>Created</dt>
                <dd className="text-ink-700">{formatDate(po.created_at)}</dd>
              </div>
              {po.needed_by ? (
                <div className="flex gap-1.5">
                  <dt>Needed by</dt>
                  <dd className="text-ink-700">{formatDate(po.needed_by)}</dd>
                </div>
              ) : null}
              {po.emailed_at ? (
                <div className="flex gap-1.5">
                  <dt>Emailed</dt>
                  <dd className="text-ink-700">{formatDate(po.emailed_at)}</dd>
                </div>
              ) : null}
              {po.acknowledged_at ? (
                <div className="flex gap-1.5">
                  <dt>Acknowledged</dt>
                  <dd className="text-ink-700">{formatDate(po.acknowledged_at)}</dd>
                </div>
              ) : null}
              {po.ready_at ? (
                <div className="flex gap-1.5">
                  <dt>Ready</dt>
                  <dd className="text-ink-700">{formatDate(po.ready_at)}</dd>
                </div>
              ) : null}
              {po.shipped_at ? (
                <div className="flex gap-1.5">
                  <dt>Shipped</dt>
                  <dd className="text-ink-700">{formatDate(po.shipped_at)}</dd>
                </div>
              ) : null}
              {po.tracking_note ? (
                <div className="flex gap-1.5">
                  <dt>Tracking</dt>
                  <dd className="text-ink-700">{po.tracking_note}</dd>
                </div>
              ) : null}
              {po.received_at ? (
                <div className="flex gap-1.5">
                  <dt>Received</dt>
                  <dd className="text-ink-700">{formatDate(po.received_at)}</dd>
                </div>
              ) : null}
              {po.receipt_note ? (
                <div className="flex gap-1.5">
                  <dt>Receipt note</dt>
                  <dd className="text-ink-700">{po.receipt_note}</dd>
                </div>
              ) : null}
            </dl>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {action ? (
              <Button
                disabled={busy}
                loading={busy}
                onClick={() => void advance(action.next, action.label)}
              >
                {action.label}
              </Button>
            ) : null}
            {/* Dropship: when ready, show "Mark shipped" + optional tracking. */}
            {po.status === "ready" && isDropship ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  placeholder="Tracking reference (optional)"
                  value={trackingNote}
                  onChange={(e) => setTrackingNote(e.target.value)}
                  className="rounded border border-ink-300 px-2 py-1 text-body-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <Button
                  disabled={busy}
                  loading={busy}
                  onClick={() => void ship()}
                >
                  Mark shipped
                </Button>
              </div>
            ) : null}
            {/* Depot: when ready, show static copy — WMart collects. */}
            {po.status === "ready" && !isDropship ? (
              <p className="text-body-sm text-ink-500">
                WMart will collect/receive these goods.
              </p>
            ) : null}
            {/* The PDF is rendered asynchronously by the worker after the PO is
                created; `pdf_ready` flips true once it's stored. Disable the
                button until then so an eager click can't hit the 404. The poll
                effect re-loads the detail until it's ready. */}
            <Button
              variant="secondary"
              disabled={po.pdf_ready === false}
              onClick={() =>
                window.open(
                  `${API_BASE}/v1/supplier/orders/${po.id}/pdf`,
                  "_blank",
                  "noopener",
                )
              }
            >
              {po.pdf_ready === false ? "Preparing PDF…" : "Download PDF"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {po.lines.length === 0 ? (
          <p className="p-6 text-body-sm text-ink-500">No lines.</p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>SKU</TH>
                <TH>Item</TH>
                <TH>Pack</TH>
                <TH className="text-right">Qty</TH>
              </TR>
            </THead>
            <TBody>
              {/* Index key — line SKU uniqueness is not type-guaranteed. */}
              {po.lines.map((line, i) => (
                <TR key={i}>
                  <TD className="font-mono text-body-sm">{line.sku}</TD>
                  <TD>{line.title}</TD>
                  <TD className="text-body-sm text-ink-500">
                    {line.pack_size ?? "—"}
                  </TD>
                  <TD className="text-right">{line.quantity}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
